import { Controller } from "@hotwired/stimulus"

/**
 * Working Hours Warning Controller
 * 
 * Affiche dynamiquement une alerte si le rendez-vous est en dehors des horaires
 * de travail du prestataire sélectionné.
 * 
 * Targets:
 *   - providerSelect: Le select du prestataire
 *   - dateField: Le champ de date
 *   - startTimeField: Le champ d'heure de début
 *   - durationField: Le champ de durée
 *   - warningContainer: Zone où afficher l'alerte
 * 
 * Values:
 *   - providerSchedules: Object JSON avec les horaires de chaque prestataire
 *     Format: { providerId: { schedule: { monday: { enabled, time_slots: [{ start_time, end_time }] }, ... } } }
 */
export default class extends Controller {
  static targets = ["providerSelect", "dateField", "startTimeField", "durationField", "warningContainer"]
  static values = { 
    providerSchedules: Object
  }

  connect() {
    console.log("WorkingHoursWarning controller connected")
    console.log("Provider schedules:", this.providerSchedulesValue)
    
    // Vérifier immédiatement si des valeurs sont déjà renseignées
    this.checkWorkingHours()
  }

  /**
   * Appelé lors du changement de prestataire, date, heure ou durée
   */
  checkWorkingHours() {
    console.log("Checking working hours...")
    
    if (!this.hasProviderSelectTarget || !this.hasDateFieldTarget || 
        !this.hasStartTimeFieldTarget || !this.hasDurationFieldTarget ||
        !this.hasWarningContainerTarget) {
      console.log("Missing required targets, skipping check")
      return
    }

    const providerId = this.providerSelectTarget.value
    const date = this.dateFieldTarget.value
    const startTime = this.startTimeFieldTarget.value
    const duration = parseInt(this.durationFieldTarget.value, 10)

    console.log(`Provider: ${providerId}, Date: ${date}, Time: ${startTime}, Duration: ${duration}`)

    // Si des valeurs manquent, masquer l'alerte
    if (!providerId || !date || !startTime || !duration) {
      this.hideWarning()
      return
    }

    // Récupérer les horaires du prestataire
    const providerData = this.providerSchedulesValue[providerId.toString()]
    if (!providerData || !providerData.schedule) {
      console.log("No schedule data for provider:", providerId)
      this.hideWarning()
      return
    }

    // Vérifier si le créneau est dans les horaires
    const isOutsideWorkingHours = this.isOutsideWorkingHours(
      providerData.schedule,
      date,
      startTime,
      duration
    )

    console.log("Is outside working hours:", isOutsideWorkingHours)

    if (isOutsideWorkingHours) {
      this.showWarning()
    } else {
      this.hideWarning()
    }
  }

  /**
   * Vérifie si le créneau est en dehors des horaires de travail
   */
  isOutsideWorkingHours(schedule, dateStr, startTimeStr, durationMinutes) {
    // Obtenir le jour de la semaine
    const date = new Date(dateStr)
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday']
    const dayName = dayNames[date.getDay()]
    
    console.log(`Checking schedule for ${dayName}`)
    
    const daySchedule = schedule[dayName]
    
    // Si pas de planning pour ce jour ou jour désactivé
    if (!daySchedule || !daySchedule.enabled) {
      console.log(`Day ${dayName} is not enabled`)
      return true
    }
    
    const timeSlots = daySchedule.time_slots || []
    if (timeSlots.length === 0) {
      console.log(`No time slots for ${dayName}`)
      return true
    }
    
    // Parser l'heure de début et calculer l'heure de fin
    const [startHours, startMinutes] = startTimeStr.split(':').map(Number)
    const appointmentStartMinutes = startHours * 60 + startMinutes
    const appointmentEndMinutes = appointmentStartMinutes + durationMinutes
    
    console.log(`Appointment: ${appointmentStartMinutes} - ${appointmentEndMinutes} minutes from midnight`)
    
    // Vérifier si le rendez-vous est entièrement contenu dans au moins une plage horaire
    const isWithinSlot = timeSlots.some(slot => {
      const [slotStartHours, slotStartMinutes] = slot.start_time.split(':').map(Number)
      const [slotEndHours, slotEndMinutes] = slot.end_time.split(':').map(Number)
      
      const slotStartTotalMinutes = slotStartHours * 60 + slotStartMinutes
      const slotEndTotalMinutes = slotEndHours * 60 + slotEndMinutes
      
      console.log(`Slot: ${slotStartTotalMinutes} - ${slotEndTotalMinutes} minutes from midnight`)
      
      // Le rendez-vous doit être entièrement dans la plage
      const isWithin = appointmentStartMinutes >= slotStartTotalMinutes && 
                       appointmentEndMinutes <= slotEndTotalMinutes
      
      console.log(`Is within this slot: ${isWithin}`)
      
      return isWithin
    })
    
    return !isWithinSlot
  }

  /**
   * Affiche l'alerte hors-horaires
   */
  showWarning() {
    if (!this.hasWarningContainerTarget) return
    
    this.warningContainerTarget.innerHTML = `
      <div class="rounded-md bg-amber-50 p-4">
        <div class="flex">
          <div class="flex-shrink-0">
            <svg class="h-5 w-5 text-amber-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
              <path d="m21.73 18-8-14a2 2 0 0 0-3.48 0l-8 14A2 2 0 0 0 4 21h16a2 2 0 0 0 1.73-3Z"/>
              <line x1="12" x2="12" y1="9" y2="13"/>
              <line x1="12" x2="12.01" y1="17" y2="17"/>
            </svg>
          </div>
          <div class="ml-3">
            <h3 class="text-sm font-medium text-amber-800">Rendez-vous hors horaires</h3>
            <div class="mt-1 text-sm text-amber-700">
              <p>Ce créneau est en dehors des horaires de travail du prestataire ou durant un congé.</p>
            </div>
          </div>
        </div>
      </div>
    `
    this.warningContainerTarget.classList.remove("hidden")
  }

  /**
   * Masque l'alerte
   */
  hideWarning() {
    if (!this.hasWarningContainerTarget) return
    
    this.warningContainerTarget.innerHTML = ""
    this.warningContainerTarget.classList.add("hidden")
  }
}
